import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import ActivityStatus from "@mapmycustomers/shared/enum/activity/ActivityStatus";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { Activity } from "@mapmycustomers/shared/types/entity";
import { IHumanReadableFilterConfig } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import getActivityStatus from "@app/util/activity/getActivityStatus";
import { METERS_IN_MILE } from "@app/util/consts";
import styles from "@app/util/fieldModel/FieldStyle.module.scss";
import CheckInLocationField from "@app/util/fieldModel/impl/CheckInLocationField";
import { DEFAULT_EMPTINESS_OPERATOR } from "@app/util/filters/defaultFilters";
import DurationFilter, { DURATION_FILTER_OPERATORS } from "@app/util/filters/DurationFilter";
import DurationRangeFilter from "@app/util/filters/DurationRangeFilter";
import EmptyFilter from "@app/util/filters/EmptyFilter";
import getEntityFilter, { ENTITY_FILTER_OPERATORS } from "@app/util/filters/EntityFilter";
import { parseApiDate } from "@app/util/parsers";
import { getActivityStatusDisplayName } from "@app/util/ui";
import getVisibilityDisplayName from "@app/util/ui/getVisibilityDisplayName";

import { formatDuration } from "../formatters";

import ActivityFieldName from "./ActivityFieldName";
import ActivityNameField from "./impl/ActivityNameField";
import ActivityNoteField from "./impl/ActivityNoteField";
import ActivityTypeField from "./impl/ActivityTypeField";
import DateField from "./impl/DateField";
import DateTimeField from "./impl/DateTimeField";
import FieldModel from "./impl/FieldModel";
import { createField } from "./impl/fieldModelUtil";
import {
  booleanFormatter,
  dateFormatter,
  distanceFormatter,
  filesFormatter,
  getFilesCsvFormatter,
  getUserTeams,
  listFormatter,
  teamsFormatter,
  timeFormatter,
} from "./impl/fieldUtil";
import SourceField from "./impl/SourceField";
import TeamField from "./impl/TeamField";
import UniqueIdField from "./impl/UniqueIdField";
import UserField from "./impl/UserField";
import addressFieldsMessages from "./util/addressFieldsMessages";

// ActivityFieldName now lives in its own module so it can be imported without pulling the whole
// field-model graph. Re-exported here for back-compat.
export { ActivityFieldName };

export const displayNames = defineMessages<ActivityFieldName>({
  [ActivityFieldName.ACCOUNT_IMPORT_ID]: {
    id: "activity.field.account.importId",
    defaultMessage: "Company ID",
    description: "CompanyId field of activity",
  },
  [ActivityFieldName.ACTIONS]: {
    id: "activity.field.actions",
    defaultMessage: "Actions",
    description: "Actions field of activity",
  },
  [ActivityFieldName.ACTIVITY_TYPE]: {
    id: "activity.field.activityType",
    defaultMessage: "Activity Type",
    description: "Activity type field of activity",
  },
  [ActivityFieldName.ASSIGNEE]: {
    id: "activity.field.assignee",
    defaultMessage: "Assignee",
    description: "Assignee field of activity",
  },
  [ActivityFieldName.ASSIGNEE_EMAIL]: {
    id: "activity.field.assigneeEmail",
    defaultMessage: "Assignee Email",
    description: "Assignee email field of activity",
  },
  [ActivityFieldName.ASSIGNEE_NAME]: {
    id: "activity.field.assigneeName",
    defaultMessage: "Assignee Name",
    description: "Assignee name field of activity",
  },
  [ActivityFieldName.ASSIGNEE_TEAM_NAME]: {
    id: "activity.field.assigneeTeamName",
    defaultMessage: "Assignee Team Name",
    description: "Assignee team name field of activity",
  },
  [ActivityFieldName.CHECK_IN_DISTANCE]: {
    id: "activity.field.checkInDistanceDelta",
    defaultMessage: "Check-In Distance Delta",
    description: "Check in distance delta field of activity",
  },
  [ActivityFieldName.CHECK_IN_TIME]: {
    id: "activity.field.checkInTime",
    defaultMessage: "Check-In Time",
    description: "Check in time field of activity",
  },
  [ActivityFieldName.CHECK_OUT_TIME]: {
    id: "activity.field.checkOutTime",
    defaultMessage: "Check-Out Time",
    description: "Check out time field of activity",
  },
  [ActivityFieldName.COMPANY]: {
    id: "activity.field.account",
    defaultMessage: "Company",
    description: "Account field of activity",
  },
  [ActivityFieldName.COMPANY_ADDRESS]: {
    id: "activity.field.companyAddress",
    defaultMessage: "Company Address",
    description: "Company address field of activity",
  },
  [ActivityFieldName.COMPLETED]: {
    id: "activity.field.completed",
    defaultMessage: "Done",
    description: "Done field of activity",
  },
  [ActivityFieldName.COMPLETED_AT]: {
    id: "activity.field.completedAt",
    defaultMessage: "Done At",
    description: "Done at field of activity",
  },
  [ActivityFieldName.COMPLETED_DATE]: {
    id: "activity.field.completedDate",
    defaultMessage: "Date Done",
    description: "Completed date field of activity",
  },
  [ActivityFieldName.COMPLETED_TIME]: {
    id: "activity.field.completedTime",
    defaultMessage: "Time Done",
    description: "Done time field of activity",
  },
  [ActivityFieldName.CONTACT_IMPORT_ID]: {
    id: "activity.field.person.importId",
    defaultMessage: "Person ID",
    description: "PersonId field of activity",
  },
  [ActivityFieldName.CREATED_AT]: {
    id: "activity.field.createdAt",
    defaultMessage: "Date Created",
    description: "Created at field of activity",
  },
  [ActivityFieldName.CREATOR]: {
    id: "activity.field.creator",
    defaultMessage: "Creator",
    description: "Creator field of activity",
  },
  [ActivityFieldName.CREATOR_EMAIL]: {
    id: "activity.field.creatorEmail",
    defaultMessage: "Creator Email",
    description: "Creator Email field of activity",
  },
  [ActivityFieldName.CREATOR_NAME]: {
    id: "activity.field.creatorName",
    defaultMessage: "Creator Name",
    description: "Creator Name field of activity",
  },
  [ActivityFieldName.CRM_LINK]: {
    id: "activity.field.crmLink",
    defaultMessage: "CRM Link",
    description: "CRM link field of activity",
  },
  [ActivityFieldName.DEAL]: {
    id: "activity.field.deal",
    defaultMessage: "Deal",
    description: "Deal field of activity",
  },
  [ActivityFieldName.DEAL_IMPORT_ID]: {
    id: "activity.field.deal.importId",
    defaultMessage: "Deal ID",
    description: "DealId field of activity",
  },
  [ActivityFieldName.DURATION]: {
    id: "activity.field.duration",
    defaultMessage: "Duration",
    description: "Duration field of activity",
  },
  [ActivityFieldName.END_AT]: {
    id: "activity.field.endAt",
    defaultMessage: "End At",
    description: "End at field of activity",
  },
  [ActivityFieldName.END_DATE]: {
    id: "activity.field.endDate",
    defaultMessage: "End Date",
    description: "End date field of activity",
  },
  [ActivityFieldName.END_TIME]: {
    id: "activity.field.endTime",
    defaultMessage: "End Time",
    description: "End time field of activity",
  },
  [ActivityFieldName.FILES]: {
    id: "activity.field.files",
    defaultMessage: "Files",
    description: "Files field of activity",
  },
  [ActivityFieldName.FREQUENCY]: {
    id: "activity.field.frequency",
    defaultMessage: "Frequency",
    description: "Frequency field of activity",
  },
  [ActivityFieldName.GEOPOINT_CHECK_IN]: {
    id: "activity.field.geopointCheckIn",
    defaultMessage: "Location at Check-In",
    description: "GeoPoint check in field of activity",
  },
  [ActivityFieldName.NAME]: {
    id: "activity.field.name",
    defaultMessage: "Activity Name",
    description: "Name field of activity",
  },
  [ActivityFieldName.NOTE]: {
    id: "activity.field.note",
    defaultMessage: "Note",
    description: "Note field of activity",
  },
  [ActivityFieldName.OWNER_EMAIL]: {
    id: "activity.field.userEmail",
    defaultMessage: "Owner Email",
    description: "Creator Email field of activity",
  },
  [ActivityFieldName.OWNER_NAME]: {
    id: "activity.field.userName",
    defaultMessage: "Owner Name",
    description: "Owner name field of activity",
  },
  [ActivityFieldName.PEOPLE]: {
    id: "activity.field.people",
    defaultMessage: "People",
    description: "People field of activity (used when multiContactActivities add-on is enabled)",
  },
  [ActivityFieldName.PERSON]: {
    id: "activity.field.contact",
    defaultMessage: "Person",
    description: "Contact field of activity",
  },
  [ActivityFieldName.PERSON_ADDRESS]: {
    id: "activity.field.personAddress",
    defaultMessage: "Person Address",
    description: "Person address field of activity",
  },
  [ActivityFieldName.PRIMARY_ASSOCIATION]: {
    id: "activity.field.primaryAssociation",
    defaultMessage: "Primary Association",
    description: "DealId field of activity",
  },
  [ActivityFieldName.QUICK_FILTER]: {
    id: "activity.field.quickFilter",
    defaultMessage: "",
    description: "Quick filter field of activity",
  },
  [ActivityFieldName.RELIABILITY]: {
    id: "activity.field.reliability",
    defaultMessage: "Verified",
    description: "Reliability field of activity",
  },
  [ActivityFieldName.SOURCE]: {
    id: "activity.field.source",
    defaultMessage: "Source",
    description: "Source field of activity",
  },
  [ActivityFieldName.START_AT]: {
    id: "activity.field.startAt",
    defaultMessage: "Date of activity",
    description: "Start at field of activity",
  },
  [ActivityFieldName.START_DATE]: {
    id: "activity.field.startDate",
    defaultMessage: "Start Date",
    description: "Start date field of activity",
  },
  [ActivityFieldName.START_TIME]: {
    id: "activity.field.startTime",
    defaultMessage: "Start Time",
    description: "Start time field of activity",
  },
  [ActivityFieldName.STATUS]: {
    id: "activity.field.status",
    defaultMessage: "Status",
    description: "Status field of activity",
  },
  [ActivityFieldName.TEAM]: {
    id: "activity.field.team",
    defaultMessage: "Team",
    description: "Team field of activity",
  },
  [ActivityFieldName.UNIQUE_ID]: {
    id: "activity.field.uniqueId",
    defaultMessage: "Unique ID",
    description: "Unique id field of activity",
  },
  [ActivityFieldName.UNRELIABLE_REASONS]: {
    id: "activity.field.unreliableReasons",
    defaultMessage: "Unverified Reason",
    description: "Unverified Reasons field of activity",
  },
  [ActivityFieldName.UPDATED_AT]: {
    id: "activity.field.updatedAt",
    defaultMessage: "Date Last Modified",
    description: "Updated at field of activity",
  },
  [ActivityFieldName.USER]: {
    id: "activity.field.user",
    defaultMessage: "Owner",
    description: "User field of activity",
  },
  [ActivityFieldName.VISIBILITY]: {
    id: "activity.field.visibility",
    defaultMessage: "Visibility",
    description: "Visibility field of activity",
  },
});

const additionalDisplayNames = defineMessages({
  companyId: {
    id: "activity.field.account.importName",
    defaultMessage: "Company - ID",
    description: "Import name for the account field of activity",
  },
  dealId: {
    id: "activity.field.deal.importName",
    defaultMessage: "Deal - ID",
    description: "Import name for the deal field of activity",
  },
  peopleIds: {
    id: "activity.field.contacts.importName",
    defaultMessage: "People - IDs",
    description: "Import name for the people (contacts) field of activity",
  },
  personId: {
    id: "activity.field.contact.importName",
    defaultMessage: "Person - ID",
    description: "Import name for the contact field of activity",
  },
});

// The removed "Owner Team Name" field showed the same owner teams as Team
const legacyFieldNames = { userTeamName: ActivityFieldName.TEAM };

const activityFieldModel = new FieldModel(
  [
    new ActivityNameField({
      displayName: displayNames[ActivityFieldName.NAME],
      displayOrder: 1,
      formProperties: { fullWidth: false },
      importProperties: { required: true },
      name: ActivityFieldName.NAME,
    }),
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: { empty: EmptyFilter, entity: getEntityFilter(EntityType.COMPANY) },
        operators: [...ENTITY_FILTER_OPERATORS, ...DEFAULT_EMPTINESS_OPERATOR],
      } as IHumanReadableFilterConfig,
      customGridProperties: {
        cellRenderer: "relationshipCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.COMPANY],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.SUPPORTS_SET_FILTER,
      ],
      filterType: FieldType.ID,
      importProperties: {
        displayName: additionalDisplayNames.companyId,
        name: "accountId",
        required: false,
      },
      name: ActivityFieldName.COMPANY,
      platformFilterNames: ["account.id", "accountId"],
      platformName: "accountId",
      type: FieldType.STRING,
      valueGetter: ["account", "name"],
    },
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: { entity: getEntityFilter(EntityType.PERSON) },
        operators: ENTITY_FILTER_OPERATORS,
      },
      customGridProperties: {
        cellRenderer: "peopleCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.PERSON],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.SINGLE_CONTACT_ONLY,
      ],
      filterType: FieldType.ID,
      // PERSON and PEOPLE are mutually exclusive via the multiContactActivities
      // field filter, so exactly one of them is importable at a time: the singular
      // contactId key while the add-on is off, the plural contacts key once it is on.
      importProperties: {
        displayName: additionalDisplayNames.personId,
        name: "contactId",
        required: false,
      },
      name: ActivityFieldName.PERSON,
      // contacts.id is the multi-contact wire name; accept it so saved filters
      // created under multiContactActivities=ON still resolve after the org
      // toggles the add-on off.
      platformFilterNames: ["contact.id", "contactId", "contacts.id"],
      platformName: "contactId",
      type: FieldType.STRING,
      valueGetter: ["contact", "name"],
    },
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: { entity: getEntityFilter(EntityType.PERSON) },
        operators: ENTITY_FILTER_OPERATORS,
      },
      customGridProperties: {
        cellRenderer: "peopleCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.PEOPLE],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.MULTI_CONTACT_ONLY,
      ],
      filterType: FieldType.ID,
      importProperties: {
        // Grouped at the bottom of the mapping dropdown, like "Companies - IDs".
        bottomPosition: true,
        displayName: additionalDisplayNames.peopleIds,
        name: "contacts",
        required: false,
      },
      name: ActivityFieldName.PEOPLE,
      // contact.id / contactId are the legacy single-contact wire names; accept
      // them so saved filters created before the multiContactActivities add-on
      // was enabled still resolve once the org toggles it on.
      platformFilterNames: ["contacts.id", "contact.id", "contactId"],
      platformName: "contacts",
      type: FieldType.LIST,
      valueFormatter: listFormatter("name", "; "),
    },
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: { entity: getEntityFilter(EntityType.DEAL) },
        operators: ENTITY_FILTER_OPERATORS,
      },
      customGridProperties: {
        cellRenderer: "relationshipCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.DEAL],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.SUPPORTS_SET_FILTER,
      ],
      filterType: FieldType.ID,
      importProperties: {
        displayName: additionalDisplayNames.dealId,
        name: "dealId",
        required: false,
      },
      name: ActivityFieldName.DEAL,
      platformFilterNames: ["deal.id", "dealId"],
      platformName: "dealId",
      type: FieldType.STRING,
      valueGetter: ["deal", "name"],
    },
    {
      customGridProperties: {
        cellRenderer: "primaryAssociationCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.PRIMARY_ASSOCIATION],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.PRIMARY_ASSOCIATION,
      type: FieldType.OBJECT,
    },
    new ActivityTypeField({
      displayName: displayNames[ActivityFieldName.ACTIVITY_TYPE],
      name: ActivityFieldName.ACTIVITY_TYPE,
    }),
    new UserField({
      displayName: displayNames[ActivityFieldName.CREATOR],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.CREATOR,
      platformFilterNames: ["createdBy"],
      platformName: "createdBy",
    }),
    new UserField({
      displayName: displayNames[ActivityFieldName.CREATOR_NAME],
      exportColumnName: "creator",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "creator",
      name: ActivityFieldName.CREATOR_NAME,
      platformFilterNames: ["createdBy"],
      valueGetter: (entity: unknown) => (entity as Activity).creator,
    }),
    {
      displayName: displayNames[ActivityFieldName.CREATOR_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.CREATOR_EMAIL,
      type: FieldType.STRING,
    },
    new UserField({
      displayName: displayNames[ActivityFieldName.USER],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.SORTABLE,
        FieldFeature.OWNER_FIELD,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      importProperties: {
        required: false,
      },
      name: ActivityFieldName.USER,
      platformFilterNames: ["userId", "user.id"],
      platformName: "userId",
    }),
    new UserField({
      displayName: displayNames[ActivityFieldName.OWNER_NAME],
      exportColumnName: "user",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.OWNER_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "user",
      name: ActivityFieldName.OWNER_NAME,
      platformFilterNames: ["userId", "user.id"],
      valueGetter: (entity: unknown) => (entity as Activity).user,
    }),
    {
      displayName: displayNames[ActivityFieldName.OWNER_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.OWNER_EMAIL,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[ActivityFieldName.COMPLETED],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.SORTABLE,
      ],
      name: ActivityFieldName.COMPLETED,
      type: FieldType.BOOLEAN,
      valueFormatter: booleanFormatter,
    },
    {
      displayName: displayNames[ActivityFieldName.VISIBILITY],
      name: ActivityFieldName.VISIBILITY,
      overridePhiSupport: false,
      type: FieldType.STRING,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        if (!intl) {
          return `${value}`;
        }
        return getVisibilityDisplayName(intl, value as Visibility);
      },
    },
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.CREATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: ActivityFieldName.CREATED_AT,
    }),
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.UPDATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: ActivityFieldName.UPDATED_AT,
    }),
    new DateTimeField({
      customGridProperties: {
        cellRenderer: "startAtCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.START_AT],
      displayOrder: 5,
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.ALWAYS_VISIBLE,
      ],
      name: ActivityFieldName.START_AT,
    }),
    new DateField({
      displayName: displayNames[ActivityFieldName.START_DATE],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "startAt",
      name: ActivityFieldName.START_DATE,
      valueFormatter: dateFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).startAt ? parseApiDate((entity as Activity).startAt!) : undefined,
    }),
    {
      displayName: displayNames[ActivityFieldName.START_TIME],
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.START_TIME,
      type: FieldType.STRING,
      valueFormatter: timeFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).startAt ? parseApiDate((entity as Activity).startAt!) : undefined,
    },
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.END_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.END_AT,
    }),
    new DateField({
      displayName: displayNames[ActivityFieldName.END_DATE],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "endAt",
      name: ActivityFieldName.END_DATE,
      valueFormatter: dateFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).endAt ? parseApiDate((entity as Activity).endAt!) : undefined,
    }),
    {
      displayName: displayNames[ActivityFieldName.END_TIME],
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.END_TIME,
      type: FieldType.STRING,
      valueFormatter: timeFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).endAt ? parseApiDate((entity as Activity).endAt!) : undefined,
    },
    new SourceField({
      displayName: displayNames[ActivityFieldName.SOURCE],
      name: ActivityFieldName.SOURCE,
    }),
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.COMPLETED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.COMPLETED_AT,
    }),
    new DateField({
      displayName: displayNames[ActivityFieldName.COMPLETED_DATE],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      filterName: "completedAt",
      name: ActivityFieldName.COMPLETED_DATE,
      platformFilterNames: ["completedAt"], // we can't show completedAt field in exports, but we can mimic this field to be it when filtering
      valueFormatter: dateFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).completedAt
          ? parseApiDate((entity as Activity).completedAt!)
          : undefined,
    }),
    {
      displayName: displayNames[ActivityFieldName.COMPLETED_TIME],
      features: [
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: ActivityFieldName.COMPLETED_TIME,
      type: FieldType.STRING,
      valueFormatter: timeFormatter,
      valueGetter: (entity: unknown) =>
        (entity as Activity).completedAt
          ? parseApiDate((entity as Activity).completedAt!)
          : undefined,
    },
    {
      displayName: displayNames[ActivityFieldName.STATUS],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.STATUS,
      type: FieldType.STRING,
      valueFormatter: (entity: unknown, value: unknown) => {
        const status = value as ActivityStatus;
        return i18nService.getIntl()
          ? getActivityStatusDisplayName(i18nService.getIntl()!, status)
          : "";
      },
      valueGetter: (entity: unknown) => getActivityStatus(entity as Activity),
    },
    new ActivityNoteField({
      customGridProperties: {
        cellRenderer: "noteCellRenderer",
        tooltipValueGetter: () => "", // suppress tooltip
      },
      displayName: displayNames[ActivityFieldName.NOTE],
      displayOrder: 2,
      features: [
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_PINNABLE,
      ],
      formProperties: {
        fullWidth: true,
      },
      name: ActivityFieldName.NOTE,
      overridePhiSupport: false,
      type: FieldType.STRING,
    }),
    new UserField({
      displayName: displayNames[ActivityFieldName.ASSIGNEE],
      displayOrder: 4,
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.REQUIRED,
        FieldFeature.NON_EXPORT_VIEW,
      ],
      importProperties: { name: "assigneeId" },
      name: ActivityFieldName.ASSIGNEE,
      platformFilterNames: ["assigneeId", "assignee.id"],
      platformName: "assigneeId",
    }),
    new UserField({
      displayName: displayNames[ActivityFieldName.ASSIGNEE_NAME],
      exportColumnName: "assignee",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "assignee",
      name: ActivityFieldName.ASSIGNEE_NAME,
      platformFilterNames: ["assigneeId", "assignee.id"],
      valueGetter: (entity: unknown) => (entity as Activity).assignee,
    }),
    {
      displayName: displayNames[ActivityFieldName.ASSIGNEE_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.ASSIGNEE_EMAIL,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[ActivityFieldName.ASSIGNEE_TEAM_NAME],
      exportColumnName: "assigneeTeams",
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.ASSIGNEE_TEAM_NAME,
      type: FieldType.OBJECT,
      valueFormatter: teamsFormatter,
      valueGetter: (entity: unknown) => getUserTeams((entity as Activity).assignee?.id),
    },
    {
      customFilterConfig: {
        defaultInstance: "duration",
        filterInstances: {
          duration: DurationFilter,
          durationRange: DurationRangeFilter,
          empty: EmptyFilter,
        },
        operators: [
          ...DURATION_FILTER_OPERATORS,
          { instance: "durationRange", operator: FilterOperator.IN_RANGE },
          { instance: "empty", operator: FilterOperator.EMPTY },
        ],
      } as IHumanReadableFilterConfig,
      displayName: displayNames[ActivityFieldName.DURATION],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.DURATION,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, fieldValue: unknown) => {
        const intl = i18nService.getIntl();
        if (!intl) {
          return String(fieldValue ?? 0);
        }
        return isNotEmpty(fieldValue) ? formatDuration(intl, fieldValue as number) : "";
      },
    },
    new UniqueIdField({
      displayName: displayNames[ActivityFieldName.CRM_LINK],
      features: [
        FieldFeature.EXTERNAL_URL_FIELD,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.DETAILS,
      ],
      name: ActivityFieldName.CRM_LINK,
    }),
    new UniqueIdField({
      displayName: displayNames[ActivityFieldName.UNIQUE_ID],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.DETAILS,
      ],
      name: ActivityFieldName.UNIQUE_ID,
    }),
    {
      displayName: displayNames[ActivityFieldName.FREQUENCY],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.FREQUENCY,
      type: FieldType.STRING,
    },
    new CheckInLocationField({
      displayName: displayNames[ActivityFieldName.GEOPOINT_CHECK_IN],
      name: ActivityFieldName.GEOPOINT_CHECK_IN,
    }),
    {
      csvExportProperties: {
        valueFormatter: (entity: unknown, value: unknown) => {
          if (!value && value !== 0) {
            return "";
          }
          const distanceInKm = value as number;
          if (localSettings.doesUseSiUnits()) {
            return distanceInKm.toFixed(3);
          }
          return ((distanceInKm * 1000) / METERS_IN_MILE).toFixed(3);
        },
      },
      displayName: displayNames[ActivityFieldName.CHECK_IN_DISTANCE],
      features: [
        FieldFeature.CHECK_IN,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.CHECK_IN_DISTANCE,
      type: FieldType.NUMBER,
      valueFormatter: distanceFormatter,
    },
    {
      customGridProperties: {
        cellRenderer: "reliabilityCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.RELIABILITY],
      features: [
        FieldFeature.CHECK_IN,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.RELIABILITY,
      type: FieldType.BOOLEAN,
    },
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.CHECK_IN_TIME],
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.NON_INTEGRATION,
      ],
      name: ActivityFieldName.CHECK_IN_TIME,
    }),
    new DateTimeField({
      displayName: displayNames[ActivityFieldName.CHECK_OUT_TIME],
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.FILTERABLE,
        FieldFeature.SORTABLE,
        FieldFeature.NON_INTEGRATION,
      ],
      name: ActivityFieldName.CHECK_OUT_TIME,
    }),
    // filter-only fields:
    {
      displayName: displayNames[ActivityFieldName.QUICK_FILTER],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: ActivityFieldName.QUICK_FILTER,
      type: FieldType.STRING,
    },
    new TeamField({
      displayName: displayNames[ActivityFieldName.TEAM],
      name: ActivityFieldName.TEAM,
      platformFilterNames: ["teamId", "team.id"],
    }),
    {
      displayName: addressFieldsMessages.companyStreet,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.COMPANY_ADDRESS_FIELD,
      ],
      name: ActivityFieldName.COMPANY_ADDRESS,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[ActivityFieldName.ACCOUNT_IMPORT_ID],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: "account.importId",
      type: FieldType.STRING,
      valueGetter: ["account", "importId"],
    },
    {
      displayName: displayNames[ActivityFieldName.CONTACT_IMPORT_ID],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: "contact.importId",
      type: FieldType.STRING,
      valueGetter: ["contact", "importId"],
    },
    {
      displayName: displayNames[ActivityFieldName.DEAL_IMPORT_ID],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: "deal.importId",
      type: FieldType.STRING,
      valueGetter: ["deal", "importId"],
    },
    {
      displayName: addressFieldsMessages.companyCity,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.COMPANY_ADDRESS_FIELD,
      ],
      name: "account.city",
      type: FieldType.STRING,
      valueGetter: ["account", "city"],
    },
    {
      displayName: addressFieldsMessages.companyState,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.COMPANY_ADDRESS_FIELD,
      ],
      name: "account.region",
      type: FieldType.STRING,
      valueGetter: ["account", "region"],
    },
    {
      customGridProperties: {
        cellRenderer: "countryCellRenderer",
      },
      displayName: addressFieldsMessages.companyCountry,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.COMPANY_ADDRESS_FIELD,
      ],
      name: "account.country",
      type: FieldType.STRING,
      valueGetter: ["account", "countryCode"],
    },
    {
      displayName: addressFieldsMessages.companyPostalCode,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.COMPANY_ADDRESS_FIELD,
      ],
      name: "account.postalCode",
      type: FieldType.STRING,
      valueGetter: ["account", "postalCode"],
    },
    {
      displayName: addressFieldsMessages.peopleStreet,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.PEOPLE_ADDRESS_FIELD,
      ],
      name: ActivityFieldName.PERSON_ADDRESS,
      type: FieldType.STRING,
    },
    {
      displayName: addressFieldsMessages.peopleCity,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.PEOPLE_ADDRESS_FIELD,
      ],
      name: "contact.city",
      type: FieldType.STRING,
      valueGetter: ["contact", "city"],
    },
    {
      displayName: addressFieldsMessages.peopleState,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.PEOPLE_ADDRESS_FIELD,
      ],
      name: "contact.region",
      type: FieldType.STRING,
      valueGetter: ["contact", "region"],
    },
    {
      customGridProperties: {
        cellRenderer: "countryCellRenderer",
      },
      displayName: addressFieldsMessages.peopleCountry,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.PEOPLE_ADDRESS_FIELD,
      ],
      name: "contact.country",
      type: FieldType.STRING,
      valueGetter: ["contact", "countryCode"],
    },
    {
      displayName: addressFieldsMessages.peoplePostalCode,
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.PEOPLE_ADDRESS_FIELD,
      ],
      name: "contact.postalCode",
      type: FieldType.STRING,
      valueGetter: ["contact", "postalCode"],
    },
    {
      csvExportProperties: {
        valueFormatter: getFilesCsvFormatter(EntityType.ACTIVITY),
      },
      customFilterConfig: {
        defaultInstance: "empty",
        filterInstances: { empty: EmptyFilter },
        operators: [...DEFAULT_EMPTINESS_OPERATOR],
      } as IHumanReadableFilterConfig,
      customGridProperties: {
        cellRenderer: "filesCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.FILES],
      displayOrder: 3,
      features: [
        FieldFeature.FILE_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.FILTERABLE,
        // not visible anywhere, except in forms, export, and list view
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
      ],
      formProperties: {
        fullWidth: true,
      },
      name: ActivityFieldName.FILES,
      type: FieldType.LIST,
      valueFormatter: filesFormatter,
    },
    {
      displayName: displayNames[ActivityFieldName.UNRELIABLE_REASONS],
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
      ],
      name: ActivityFieldName.UNRELIABLE_REASONS,
      type: FieldType.STRING,
    },
    // fake column, to only show actions on the List view
    {
      customGridProperties: {
        cellClass: [styles.actionsField],
        cellRenderer: "actionsCellRenderer",
      },
      displayName: displayNames[ActivityFieldName.ACTIONS],
      displayOrder: 10,
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      name: ActivityFieldName.ACTIONS,
      type: FieldType.OBJECT,
    },
  ].map(createField),
  legacyFieldNames
);

export const dateTimeColumnsToExportColumnsMap: Partial<
  Record<ActivityFieldName, ActivityFieldName[]>
> = {
  [ActivityFieldName.COMPLETED_AT]: [
    ActivityFieldName.COMPLETED_DATE,
    ActivityFieldName.COMPLETED_TIME,
  ],
  [ActivityFieldName.END_AT]: [ActivityFieldName.END_DATE, ActivityFieldName.END_TIME],
  [ActivityFieldName.START_AT]: [ActivityFieldName.START_DATE, ActivityFieldName.START_TIME],
};

export const listColumnsToExportColumnsMap: Record<string, ActivityFieldName[]> = {
  ...dateTimeColumnsToExportColumnsMap,
  [ActivityFieldName.ASSIGNEE]: [ActivityFieldName.ASSIGNEE_NAME, ActivityFieldName.ASSIGNEE_EMAIL],
  [ActivityFieldName.CREATOR]: [ActivityFieldName.CREATOR_NAME, ActivityFieldName.CREATOR_EMAIL],
  [ActivityFieldName.USER]: [ActivityFieldName.OWNER_NAME, ActivityFieldName.OWNER_EMAIL],
};

export default activityFieldModel;
